import { useCallback, useEffect, useRef, useState } from "react";
import type { PublicAudioSource } from "@hitster/shared";
import { audioManager } from "../lib/audioManager";

export interface PreviewSource {
  url: string;
  clipStartSeconds: number;
  clipDurationSeconds: number;
  sourceType: PublicAudioSource["sourceType"];
}

export function useAdminSongPreview() {
  const stopTimerRef = useRef<number | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(() => {
    if (stopTimerRef.current) {
      window.clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    audioManager.stop();
    setPlayingId(null);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const play = useCallback(
    async (id: string, source: PreviewSource) => {
      if (playingId === id) {
        stop();
        return;
      }

      stop();
      setLoadingId(id);
      setError(null);

      try {
        await audioManager.unlockWithUserGesture();
        const ok = await audioManager.play(
          {
            roundId: "admin-preview",
            url: source.url,
            clipStartSeconds: source.clipStartSeconds,
            clipDurationSeconds: source.clipDurationSeconds,
            sourceType: source.sourceType,
          },
          Date.now(),
        );

        if (!ok) {
          setError(audioManager.getVolume() === 0 ? "Volume à 0 %" : "Lecture impossible");
          return;
        }

        setPlayingId(id);
        stopTimerRef.current = window.setTimeout(
          () => stop(),
          Math.min(source.clipDurationSeconds, 20 * 60) * 1000 + 400,
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : "Lecture impossible");
        stop();
      } finally {
        setLoadingId(null);
      }
    },
    [playingId, stop],
  );

  const playFromApi = useCallback(
    async (songId: string) => {
      const res = await fetch(`/api/admin/songs/${songId}/preview`, { credentials: "include" });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? "Impossible de charger l'extrait");
      }
      const source = (await res.json()) as PreviewSource;
      await play(songId, source);
    },
    [play],
  );

  return { playingId, loadingId, error, play, playFromApi, stop, setError };
}
